
import ResponsiveTable from "../../../components/ResponsiveTable";
import { hideLoading, showLoading } from "../../../loading";
import swal from 'sweetalert';
import request from "../../../request";
import PillLabel from "../../../components/PillLabel";
import RecordLoan from "./RecordLoan";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router";
import ViewIcon from '@mui/icons-material/Visibility';
import { connect } from "react-redux";
import { LOAN_STATUS, USER_ROLE } from "../../../../../shared/constants";
import { formatMoney, getAgentFullName, getCustomerFullName, hasIntersection, isGuest } from "../../../../../shared/utils";
import SearchProduct from "../SearchProduct";
import DateTime from "../../../components/DateTime";
import StyledLink from "../../../components/StyledLink";
import links from "../../../links";
import RoundedIconButton from "../../../components/RoundedIconButton";
import { Calculate, Download } from "@mui/icons-material";
import LoanCalculator from "./LoanCalculator";
import NavigationStateSaver from "../../../components/NavigationStateSaver";
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { errorToast } from "../../../toast";
import PDF from "../../../PDF";


class _List extends NavigationStateSaver {

   state = {
      loans: null,
      page: 0,
      pageSize: 10,
      loanCount: 0,
      recordLoanDialogOpen: false,
      filters: {
         status: undefined,
         from: undefined,
         to: undefined,
         product: undefined,
         missed_payments: undefined
      },
      search: '',
      loanCalculatorDialogOpen: false,
      selected: [],
      downloadOptionsMenuAnchor: null
   }

   downloadSummary = async () => {

      try {

         showLoading();

         // fetch data
         const [ loansSummaryRes, parRes ] = await Promise.all([
            request.get('/api/general/reports/loans'),
            request.get('/api/general/reports/par')
         ]);

         const loansSummary = loansSummaryRes.data;
         const par = parRes.data;

         // create pdf
         const pdf = new PDF('Loans Summary', 'Loans Summary');


         /// count by status
         pdf.addTable({
            title: 'By Status',
            headers: ['Status', 'Count'],
            rows: Object.entries(loansSummary.count.status).map(([status, count]) => [status.toUpperCase(), count])
         });

         /// count by product type
         pdf.addTable({
            title: 'By Product Type',
            headers: ['Product', 'Count'],
            rows: Object.entries(loansSummary.count.product_type).map(([type, count]) => [type.toUpperCase(), count])
         });

         /// revenue
         pdf.addTable({
            title: 'Revenue',
            headers: ['Product Type', 'Expected', 'Collected'],
            rows: loansSummary.revenue.map(item => [item.product_type.toUpperCase(), formatMoney(item.expected), formatMoney(item.collected)])
         });

         /// PAR
         pdf.addHorizontalTable({
            title: 'Portfolio At Risk',
            data: Object
               .entries(par)
               .filter(([key]) => key !== 'overall')
               .map(([key, value]) => [key.toUpperCase(), formatMoney(value)])
               .reduce((acc, [key, value]) => {
                  acc[key] = value;
                  return acc;
               }, {}),
         });

         /// save the file
         pdf.save();

      } catch (err) {
         swal("Error", err.toString(), 'error');
      } finally {
         hideLoading();
      }
   }

   fetchLoans = async ({ download=false, selectedOnly=false }={}) => {

      try {

         showLoading();

         const limit = this.state.pageSize;
         const page = this.state.page + 1;
         const params = { page, limit };

         // if [download, selectedOnly ] are true, we need to only filter by selected loans
         // and ignore all other filters
         if (download && selectedOnly) {

            const { selected } = this.state;
            if (selected.length)
               params._id = selected.map(loan => loan._id);
            else
               return errorToast("No loans selected");

         } else {
            const { status, from, to, product, missed_payments } = this.state.filters;

            if (status) params.status = status;
            if (from) params.from = from;
            if (to) params.to = to;
            if (product) params.product = product;
            if (![ '', undefined ].includes(missed_payments)) params.missed_payments = missed_payments;

            if (this.state.search)
               params.search = this.state.search;
         }

         if (download) {
            params.download = true;
            params.ignore_pagination = true;
         }

         const path =  '/api/general/loans';

         if (download) {

            // get teh file
            const res = await request.get(path, { params, responseType: 'blob' });

            // save the file
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.setAttribute('href', url);
            a.setAttribute('download', 'Loans.xlsx');
            a.click();
         
            // clean up
            window.URL.revokeObjectURL(url);
            
            return swal("Success", "Loans downloaded successfully", 'success');

         }

         const res = await request.get(path, { params });
         const { loans, total: loanCount } = res.data;

         this.setState({ loans, loanCount });

      } catch (err) {
         swal("Error", err.toString(), 'error');
      } finally {
         hideLoading();
      }
   }

   async componentDidMount() {
      await super.componentDidMount();
      if (!this.state.loans)
         this.fetchLoans();
   }
   
   render() {

      let onAdd;
      const { roles } = this.props;

      if (hasIntersection(roles, [USER_ROLE.SUPER_ADMIN, USER_ROLE.SALES_MANAGER]))
         onAdd = () => this.setState({ recordLoanDialogOpen: true });

      let customerAgentColumns = [];

      if (!isGuest(roles)) {
         customerAgentColumns = [
            {
               field: 'customer',
               caption: 'Customer',
               render: customer => customer ? <StyledLink
                  to={links.customer(customer._id)}>
                     {getCustomerFullName(customer)}
                  </StyledLink> : '–'
            },
            {
               field: 'agent',
               caption: 'Agent',
               render: agent => agent ? <StyledLink
                  to={links.agent(agent._id)}>
                     {getAgentFullName(agent)}
                  </StyledLink> : '–'
            }
         ];
      }

      const actions = <div className="h-full flex items-center gap-2">
         <RoundedIconButton
            onClick={event => this.setState({ downloadOptionsMenuAnchor: event.currentTarget })}
         >
            <Download />
         </RoundedIconButton>
            
         <RoundedIconButton onClick={() => this.setState({ loanCalculatorDialogOpen: true })}>
            <Calculate />
         </RoundedIconButton>
      </div>

      return <>
         <ResponsiveTable
            cols={[
               {
                  field: 'loan_no',
                  caption: 'Loan No',
                  render: loan_no => <PillLabel>{loan_no}</PillLabel>
               },
               {
                  field: 'product',
                  caption: 'Product',
                  render: product => product ? <div className="max-w-[100px] truncate" title={product.name}>
                     {product.name}
                  </div> : '–'
               },
               ...customerAgentColumns,
               {
                  field: 'principal',
                  caption: 'Principal',
                  render: principal => principal ? formatMoney(principal) : '–',
                  rightAlign: true
               },
               {
                  field: 'owing',
                  caption: 'Owing',
                  render: principal => principal ? formatMoney(principal) : '–',
                  rightAlign: true
               },
               {
                  field: 'status',
                  caption: 'Status',
                  render: status => <PillLabel className="uppercase">{status}</PillLabel>
               },
               {
                  field: 'created_at',
                  caption: 'Date Applied',
                  render: date => <DateTime value={date} />
               },
               {
                  field: 'actions',
                  caption: '',
                  render: (_, loan) => <>
                     <Link to={links.loan(loan._id)}>
                        <IconButton size="small">
                           <ViewIcon />
                        </IconButton>
                     </Link>
                  </>
               }
            ]}
            rows={this.state.loans}
            fullHeight
            pagination={{
               page: this.state.page,
               count: this.state.loanCount,
               labelRowsPerPage: "Loans per page",
               rowsPerPage: this.state.pageSize,
               onPageChange: async (_, page) => {
                  await this.updateState({ page });
                  this.fetchLoans();
               },
               onRowsPerPageChange: async (event) => {
                  await this.updateState({ page: 0, pageSize: event.target.value });
                  this.fetchLoans();
               }
            }}
            onRefresh={this.fetchLoans}
            onAdd={onAdd}
            actions={actions}
            filters={{
               onChange: filters => this.setState({ filters, page: 0 }, this.fetchLoans),
               filters: [
                  {
                     field: 'status',
                     label: 'Status',
                     type: 'select',
                     options: Object.values(LOAN_STATUS).map(status => ({ value: status, label: status.toUpperCase() })),
                     value: this.state.filters.status
                  },
                  {
                     field: 'from',
                     label: 'From',
                     type: 'date',
                     value: this.state.filters.from
                  },
                  {
                     field: 'to',
                     label: 'To',
                     type: 'date',
                     value: this.state.filters.to
                  },
                  {
                     field: 'product',
                     label: 'Product',
                     type: "custom",
                     component: SearchProduct,
                     value: this.state.filters.product
                  },
                  {
                     field: "missed_payments",
                     label: "Missed Payments",
                     type: "number",
                     value: this.state.filters.missed_payments,
                  }
               ]
            }}
            search={{
               placeholder: 'Search by loan number',
               query: this.state.search,
               onChange: search => this.setState({ search }),
               onSearch: async () => {
                  await this.updateState({ page: 0 });
                  this.fetchLoans();
               }
            }}
            onSelected={selected => this.setState({ selected })}
         />
         {
            this.state.recordLoanDialogOpen && <RecordLoan
               onClose={loan => {
                  const update = { recordLoanDialogOpen: false };

                  if (loan) {
                     update.loans = [loan, ...this.state.loans];
                     update.loanCount = this.state.loanCount + 1;
                  }

                  this.setState(update);
                  
               }}
            />
         }
         {
            this.state.loanCalculatorDialogOpen && <LoanCalculator
               onClose={() => this.setState({ loanCalculatorDialogOpen: false })}
            />
         }
         {
            this.state.downloadOptionsMenuAnchor && <Menu
               anchorEl={this.state.downloadOptionsMenuAnchor}
               anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
               transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
               open
               onClose={() => this.setState({ downloadOptionsMenuAnchor: false })}
            >
               {
                  [
                     {
                        label: 'List (all)',
                        icon: <FormatListNumberedIcon />,
                        onClick: () => this.fetchLoans({ download: true })
                     },
                     {
                        label: 'List (selected)',
                        icon: <FormatListNumberedIcon />,
                        onClick: () => {
                           this.fetchLoans({ download: true, selectedOnly: true });
                        }
                     },
                     {
                        label: 'Summary',
                        icon: <PictureAsPdfIcon />,
                        onClick: this.downloadSummary
                     }
                  ]
                     .map(({ label, icon, onClick }) => <MenuItem
                        key={label}
                        onClick={() => {
                           this.setState({ downloadOptionsMenuAnchor: null });
                           onClick();
                        }}
                        className="flex items-center gap-3 px-4"
                     >
                        {icon}
                        <span className="text-gray-600">{label}</span>
                     </MenuItem>)
               }
            </Menu>
         }
      </>
   }
}

const mapStateToProps = state => ({ roles: state.user.roles });
const List = connect(mapStateToProps)(_List);

export default List;